import { Dialog } from "@headlessui/react";
import {
  CartEstimatedCost,
  CartLineImage,
  CartLinePrice,
  CartLineProductTitle,
  CartLineQuantity,
  CartLineQuantityAdjustButton,
  CartLineSelectedOptions,
  CartLines,
  useCart,
  useCartLinesTotalQuantity,
} from "@shopify/hydrogen";
import { useEffect, useState } from "react";

import { useCartUI } from "../../hooks/use-cart-ui";
import { useNavigate } from "../../hooks/use-navigate";
import type { CheckoutEnabledConfig } from "../../lookbook";
import { Modal, ModalSection } from "../Modal";
import { BUTTON_PRIMARY_CLASSES } from "./CartIconWithItemsButton";

export interface CartWithLinesModalProps {
  checkout: CheckoutEnabledConfig;
}

export function CartWithLinesModal(props: CartWithLinesModalProps) {
  const { isCartOpen, closeCart } = useCartUI();

  const numLines = useCartLinesTotalQuantity();

  const { checkout } = props;

  useEffect(() => {
    if (numLines === 0 && isCartOpen) {
      closeCart();
    }
  }, [numLines, isCartOpen, closeCart]);

  return (
    <Modal
      id="cart-with-lines-modal"
      testId="cart-with-lines-modal"
      active={isCartOpen}
      onClose={closeCart}
      header={
        <Dialog.Title className="text-lg font-medium text-gray-900">
          Shopping cart
        </Dialog.Title>
      }
      footer={<CartFooter />}>
      <ModalSection id="cart-with-lines-modal-section" title="Shopping cart">
        <CartItems checkout={checkout} />
      </ModalSection>
    </Modal>
  );
}

function CartItems(props: { checkout: CheckoutEnabledConfig }) {
  const { checkout } = props;

  return (
    <CartLines>
      {() => (
        <div className="py-6 flex">
          <div className="flex-shrink-0 w-24 h-24 border border-gray-200 rounded-md overflow-hidden">
            <CartLineImage className="w-full h-full object-center object-cover" />
          </div>
          <div className="ml-4 flex-1 flex flex-col">
            <div>
              <div className="flex justify-between text-base font-medium text-gray-900">
                <CartLineProductTitle className="text-lg font-medium" />
                <CartLinePrice className="text-lg" />
              </div>
              <CartLineSelectedOptions
                as="ul"
                className="mt-1 text-sm text-gray-500">
                {({ name, value }) => {
                  if (
                    name &&
                    checkout.hiddenProductOptionKeys.includes(
                      name.toLowerCase(),
                    )
                  ) {
                    return <></>;
                  }

                  return (
                    <>
                      {name}: {value}
                    </>
                  );
                }}
              </CartLineSelectedOptions>
            </div>
            <div className="flex-1 flex items-end justify-between text-sm">
              <CartItemQuantity />
              <div className="flex">
                <CartLineQuantityAdjustButton
                  adjust="remove"
                  aria-label="Remove from cart"
                  className="disabled:pointer-events-all disabled:cursor-wait">
                  <h3 className="font-medium text-gray-600 hover:text-gray-500">
                    Remove
                  </h3>
                </CartLineQuantityAdjustButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </CartLines>
  );
}

function CartItemQuantity() {
  return (
    <div className="flex border rounded border-gray-300 items-center overflow-auto mt-2">
      <CartLineQuantityAdjustButton
        adjust="decrease"
        aria-label="Decrease quantity"
        className="p-2 disabled:pointer-events-all disabled:cursor-wait">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-gray-400"
          viewBox="0 0 20 20"
          fill="currentColor">
          <path
            fillRule="evenodd"
            d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </CartLineQuantityAdjustButton>
      <CartLineQuantity
        as="div"
        className="p-2 text-gray-900 text-xs text-center"
      />
      <CartLineQuantityAdjustButton
        adjust="increase"
        aria-label="Increase quantity"
        className="p-2 text-gray-400 disabled:pointer-events-all disabled:cursor-wait">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor">
          <path
            fillRule="evenodd"
            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
            clipRule="evenodd"
          />
        </svg>
      </CartLineQuantityAdjustButton>
    </div>
  );
}

function CartFooter() {
  const navigate = useNavigate();

  const [requestedCheckout, setRequestedCheckout] = useState(false);

  const { closeCart } = useCartUI();

  const { status, checkoutUrl } = useCart();

  useEffect(() => {
    if (requestedCheckout && checkoutUrl && status === "idle") {
      navigate(checkoutUrl);
    }
  }, [requestedCheckout, status, navigate, checkoutUrl]);

  return (
    <div className="w-full flex flex-col items-stretch justify-start">
      <div className="flex justify-between text-base font-medium text-gray-900">
        <p>Subtotal</p>
        <CartEstimatedCost
          amountType="subtotal"
          role="cell"
          className="text-right"
        />
      </div>
      <p className="mt-0.5 text-sm text-gray-500">
        Shipping and taxes calculated at checkout.
      </p>
      <div className="mt-6">
        <button
          type="button"
          className={BUTTON_PRIMARY_CLASSES}
          data-test-id="cart-checkout-button"
          onClick={() => setRequestedCheckout(true)}>
          Checkout
        </button>
      </div>
      <div className="mt-6 flex justify-center text-sm text-center text-gray-500">
        <p>
          or{" "}
          <button
            type="button"
            className="text-gray-600 font-medium hover:text-gray-500"
            onClick={closeCart}>
            continue shopping
            <span aria-hidden="true"> &rarr;</span>
          </button>
        </p>
      </div>
    </div>
  );
}
